import React, { useEffect, useState } from 'react'

import { Stack } from '@mui/material'
import Modal2 from '@/components/Modal2'
import { Controller, useForm } from 'react-hook-form'
import { Message } from '@cx/ui'
import { useRequest } from 'ahooks'
import { getSecurityOptions, putUserPassword } from '@/api/user'
import { PasswordInput } from './common'
import SecurityVerifyPanel from './SecurityVerifyPanel'

interface PasswordModalProps {
  open: boolean
  onCancel: () => void
  onOk: () => void
  hasPassword?: boolean
  currentPhone?: string
  currentMail?: string
}

const PasswordModal: React.FC<PasswordModalProps> = ({
  open,
  onCancel,
  onOk,
  hasPassword,
  currentPhone,
  currentMail,
}) => {
  const [verifyType, setVerifyType] = useState('')
  const {
    control,
    formState: { errors },
    handleSubmit,
    watch,
    reset,
    trigger,
    setValue,
  } = useForm({
    defaultValues: {
      verify_code: '',
      verify_password: '',
      verify_state: '',
      new_password: '',
      confirm_password: '',
    },
  })

  const newPassword = watch('new_password')

  const { data: securityOptions, runAsync: loadSecurityOptions } = useRequest(() => getSecurityOptions('change_password'), {
    manual: true,
  })

  useEffect(() => {
    if (open) {
      loadSecurityOptions().then((res) => {
        setVerifyType(res?.recommended_verify_type || res?.available_verify_types?.[0] || '')
      })
    } else {
      reset()
      setVerifyType('')
    }
  }, [open, loadSecurityOptions, reset])

  const onSubmit = handleSubmit((data) => {
    putUserPassword({
      verify_type: verifyType as 'phone' | 'email' | 'password' | 'wechat',
      verify_code: data.verify_code,
      verify_password: data.verify_password,
      verify_state: data.verify_state,
      new_password: data.new_password,
      confirm_password: data.confirm_password,
    }).then(() => {
      onOk()
      onCancel()
      Message.success(hasPassword ? '修改成功，请重新登录' : '设置成功，请重新登录')
    })
  })

  return (
    <Modal2 title={hasPassword ? '修改密码' : '设置密码'} width={625} open={open} onCancel={onCancel} onOk={onSubmit}>
      <Stack gap={3}>
        <SecurityVerifyPanel
          scene='change_password'
          open={open}
          options={securityOptions}
          verifyType={verifyType}
          onVerifyTypeChange={setVerifyType}
          control={control}
          errors={errors}
          trigger={trigger}
          setValue={setValue}
          watch={watch}
          currentPhone={currentPhone}
          currentMail={currentMail}
        />
        <Controller
          rules={{
            required: '请输入新密码',
          }}
          name='new_password'
          control={control}
          render={({ field }) => (
            <PasswordInput
              required
              {...field}
              variant='outlined'
              label='新密码'
              fullWidth
              error={Boolean(errors.new_password)}
              helperText={errors.new_password?.message as string}
              size='small'
              autoComplete='off'
            />
          )}
        />
        <Controller
          rules={{
            required: '请输入确认密码',
            validate: (value) => value === newPassword || '两次密码不一致',
          }}
          name='confirm_password'
          control={control}
          render={({ field }) => (
            <PasswordInput
              required
              {...field}
              variant='outlined'
              label='确认密码'
              fullWidth
              error={Boolean(errors.confirm_password)}
              helperText={errors.confirm_password?.message as string}
              size='small'
              autoComplete='off'
            />
          )}
        />
      </Stack>
    </Modal2>
  )
}

export default PasswordModal
